import type React from "react";
import type { LazyImportFunction } from "./types";

/**
 * @description Retry configuration
 */
interface RetryConfig {
  attempts: number;
  delay: number;
  backoff?: boolean;
}

/**
 * @description Default retry configuration
 */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  attempts: 3,
  delay: 1000,
  backoff: true,
};

/**
 * @description Sleep utility function
 */
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @description Empty fallback component for failed lazy loads
 */
const EmptyFallback: React.ComponentType<any> = () => null;

/**
 * @description Retry wrapper for lazy import functions
 * @param importFn - The import function to retry
 * @param config - Retry configuration
 * @returns Promise that resolves to the imported component or empty fallback
 */
export async function withRetry<T extends Record<string, unknown> = Record<string, unknown>>(
  importFn: LazyImportFunction<T>,
  config: Partial<RetryConfig> = {},
): Promise<{ default: React.ComponentType<T> }> {
  const { attempts, delay, backoff } = { ...DEFAULT_RETRY_CONFIG, ...config };

  let lastError: Error = new Error("Unknown error");

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await importFn();
    } catch (error) {
      lastError = error as Error;

      console.warn(`Lazy load attempt ${attempt}/${attempts} failed:`, error);

      // Don't wait after the last attempt
      if (attempt < attempts) {
        const waitTime = backoff ? delay * attempt : delay;
        await sleep(waitTime);
      }
    }
  }

  // Instead of throwing an error, return an empty fallback component
  // This ensures the page continues to work even if a component fails to load
  console.error("All lazy load attempts failed, using empty fallback:", lastError);

  return {
    default: EmptyFallback as React.ComponentType<T>,
  };
}

/**
 * @description Create a retry-enabled import function
 * @param importFn - The original import function
 * @param config - Retry configuration
 * @returns New import function with retry capability
 */
export function createRetryImport<T extends Record<string, unknown> = Record<string, unknown>>(
  importFn: LazyImportFunction<T>,
  config?: Partial<RetryConfig>,
): LazyImportFunction<T> {
  return () => withRetry(importFn, config);
}
